'use client';
import { useTranslations } from 'next-intl';
import { CalendarOptions, currentCalendarYear, calendarMonthNumbers } from '@freealbum/document';
import { Input, SegmentedControl } from '@freealbum/ui';

export function CalendarSetup({ value, onChange }: { value: CalendarOptions; onChange: (value: CalendarOptions) => void }) {
  const t = useTranslations('newAlbum.calendar');
  const valid = CalendarOptions.safeParse(value).success;
  const patch = (change: Partial<CalendarOptions>) => onChange({ ...value, ...change });
  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-ink-2">{t('lead')}</p>
      <SegmentedControl aria-label={t('system')} value={value.system}
        onValueChange={(system: CalendarOptions['system']) => patch({ system, year: currentCalendarYear(system) })}
        options={[{ value: 'gregorian', label: t('gregorian') }, { value: 'hebrew', label: t('hebrew') }]} fullWidth />
      <Input label={t('year')} type="number" inputMode="numeric" dir="ltr" value={value.year || ''}
        min={value.system === 'hebrew' ? 5660 : 1900} max={value.system === 'hebrew' ? 5900 : 2100}
        onChange={(e) => patch({ year: Number(e.target.value) })}
        hint={t(value.system === 'hebrew' ? 'hebrewYearHint' : 'gregorianYearHint')}
        error={valid ? undefined : t('invalidYear')} />
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-2 text-sm font-medium text-ink">{t('datesAndHolidays')}</legend>
        {(['dualDates', 'jewishHolidays', 'israeliHolidays'] as const).map((key) => (
          <label key={key} className="flex min-h-10 items-center gap-3 text-sm text-ink">
            <input type="checkbox" className="size-4 accent-accent" checked={value[key]} onChange={(e) => patch({ [key]: e.target.checked })} />
            {t(key)}
          </label>
        ))}
      </fieldset>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-ink">{t('region')}</span>
        <SegmentedControl aria-label={t('region')} value={value.region} onValueChange={(region: CalendarOptions['region']) => patch({ region })}
          options={[{ value: 'israel', label: t('israel') }, { value: 'diaspora', label: t('diaspora') }]} fullWidth />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-ink">{t('weekStart')}</span>
        <SegmentedControl aria-label={t('weekStart')} value={String(value.weekStart)} onValueChange={(v) => patch({ weekStart: v === '1' ? 1 : 0 })}
          options={[{ value: '0', label: t('sunday') }, { value: '1', label: t('monday') }]} fullWidth />
      </div>
      {valid ? <p role="status" className="rounded-md bg-paper-2 p-3 text-sm text-ink-2">{t('monthCount', { count: calendarMonthNumbers(value).length })}{calendarMonthNumbers(value).length === 13 ? ` ${t('leapYearNote')}` : ''}</p> : null}
      <p className="text-xs text-ink-3">{t('eveningNote')}</p>
    </div>
  );
}
